import { z } from "zod/v4";

import { AnalyticsEventDtoSchema } from "./analytics-events";
import {
  CustomEventAddToCartSettingsDtoSchema,
  CustomEventBeginCheckoutSettingsDtoSchema,
  CustomEventOrderPlacedSettingsDtoSchema,
  CustomEventVisitedAnyCollectionSettingsDtoSchema,
  CustomEventVisitedAnyPdpSettingsDtoSchema,
} from "./custom-event-settings";

export const StandardEventDtoSchema = AnalyticsEventDtoSchema.extend({
  isStandard: z.literal(true),
  hasImpactCalculation: z.boolean(),
});

export type StandardEventDto = z.infer<typeof StandardEventDtoSchema>;

export const StandardEventDisallowedKeyMetricsList = [
  "pctDiscountedOrders",
  "nOrdersDiscounted",
  "discountPctDiscountedOrderGrossRevenue",
  "discountPctDiscountedOrderGrossProfit",
  "discountPctTotalGrossProfit",
  "discountPctTotalGrossRevenue",
  "netProductRevenue",
  "totalCogs",
  "productProfit",
  "netShippingRevenue",
  "totalShippingCost",
  "shippingProfit",
  "totalTransactionFee",
  "totalTaxIncluded",
  "netRevenue",
  "totalCost",
  "totalProfit",
  "grossMarginPct",
  "nVisitors",
  "nOrders",
  "subscriptionShippingRevenuePerOrder",
  "subscriptionUnitsPerSubscriptionOrder",
  "subscriptionProductRevenuePerUnit",
  "nSubscriptionOrders",
  "subscriptionShippingRevenue",
  "subscriptionProductRevenue",
  "totalSubscriptionProfit",
  "netSubscriptionRevenue",
  "subscriptionUnits",
  "nVisitorsAddedToCart",
  "nVisitorsCheckoutStarted",
  "nVisitorsAbandonedCart",
  "nVisitorsAbandonedCheckout",
  "nVisitorsCheckoutContactInfoSubmitted",
  "nVisitorsCheckoutAddressInfoSubmitted",
  "nVisitorsViewedProductPage",
  "nVisitorsViewedCollectionPage",
  "pctOrdersWithUpsell",
  "nOrdersWithUpsellProduct",
  "netRevenueFromUpsell",
  "pctOrdersWithOnsiteUpsell",
  "nOrdersWithOnsiteUpsellProduct",
  "netRevenueFromOnsiteUpsell",
  // Post-test metrics
  "nCustomers",
  "nPostOrders",
  "postNetRevenue",
  "pctRepeatCustomers",
  "postGrossProfit",
  "postOrdersPerCustomer",
  "postOrdersPerVisitor",
  "postProfitPerCustomer",
  "postProfitPerVisitor",
  "postRevenuePerCustomer",
  "postRevenuePerVisitor",
] as const;

export const StandardEventAllowedKeyMetricsList = [
  "conversionRate",
  "addToCartRate",
  "abandonedCartRate",
  "abandonedCheckoutRate",
  "checkoutBeginRate",
  "checkoutEnterContactInfoRate",
  "checkoutAddressSubmittedRate",
  "viewProductPageRate",
  "viewCollectionPageRate",
  "netRevenuePerVisitor",
  "netProductRevenuePerOrder",
  "netRevenuePerOrder",
  "avgUnitsPerOrder",
  "avgProductRevenuePerUnit",
  "profitPerVisitor",
  "profitPerOrder",
  "pctOrdersFreeShipping",
  "netShippingRevenuePerOrder",
  "subscriptionOrdersPerVisitor",
  "subscriptionRevenuePerVisitor",
  "subscriptionProfitPerVisitor",
  "subscriptionProductRevenuePerOrder",
  "avgDiscountPerDiscountedOrder",
  "avgDiscountPerAllOrders",
  "pctSubscriptionOrders",
  "subscriptionRevenuePerOrder",
  "pctOrdersWithPostPurchaseOffer",
  "netRevenueFromPostPurchaseOffer",
  "pctOrdersWithUpsell",
  "netRevenueFromUpsell",
  "pctOrdersWithOnsiteUpsell",
  "netRevenueFromOnsiteUpsell",
] as const;

export type StandardEventAllowedKeyMetrics =
  (typeof StandardEventAllowedKeyMetricsList)[number];

export type StandardEventDisallowedKeyMetrics =
  (typeof StandardEventDisallowedKeyMetricsList)[number];

export const StandardEventList = [
  ...StandardEventAllowedKeyMetricsList,
  ...StandardEventDisallowedKeyMetricsList,
] as const;

export type StandardEventType = (typeof StandardEventList)[number];

export const AddToCartCustomEventDtoSchema = StandardEventDtoSchema.extend({
  type: z.literal("addToCartCustomEvent"),
  settings: CustomEventAddToCartSettingsDtoSchema,
});

export type AddToCartCustomEvent = z.infer<
  typeof AddToCartCustomEventDtoSchema
>;

export const BeginCheckoutCustomEventDtoSchema = StandardEventDtoSchema.extend({
  type: z.literal("beginCheckoutCustomEvent"),
  settings: CustomEventBeginCheckoutSettingsDtoSchema,
});

export type BeginCheckoutCustomEvent = z.infer<
  typeof BeginCheckoutCustomEventDtoSchema
>;

export const OrderPlacedCustomEventDtoSchema = StandardEventDtoSchema.extend({
  type: z.literal("orderPlacedCustomEvent"),
  settings: CustomEventOrderPlacedSettingsDtoSchema,
});

export type OrderPlacedCustomEvent = z.infer<
  typeof OrderPlacedCustomEventDtoSchema
>;

export const VisitedAnyPdpCustomEventDtoSchema = StandardEventDtoSchema.extend({
  type: z.literal("visitedAnyPdpCustomEvent"),
  settings: CustomEventVisitedAnyPdpSettingsDtoSchema,
});

export type VisitedAnyPdpCustomEvent = z.infer<
  typeof VisitedAnyPdpCustomEventDtoSchema
>;

export const VisitedAnyCollectionCustomEventDtoSchema =
  StandardEventDtoSchema.extend({
    type: z.literal("visitedAnyCollectionCustomEvent"),
    settings: CustomEventVisitedAnyCollectionSettingsDtoSchema,
  });

export type VisitedAnyCollectionCustomEvent = z.infer<
  typeof VisitedAnyCollectionCustomEventDtoSchema
>;

const StandardEventAnalyticsMetricSchema = z.object({
  analyticsMetricId: z.string(),
  analyticsDataType: z.string(),
  isEligibleForPrimaryMetric: z.boolean(),
});

type StandardEventAnalyticsMetricType = z.infer<
  typeof StandardEventAnalyticsMetricSchema
>;

export const STANDARD_EVENTS_ALLOWED_KEY_METRICS: Record<
  StandardEventAllowedKeyMetrics,
  StandardEventDto & StandardEventAnalyticsMetricType
> = {
  conversionRate: {
    isStandard: true,
    id: "conversionRate",
    identifier: "conversionRate",
    name: "Conversion Rate",
    description: "Orders divided by unique visitors",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "conversion_rate",
    analyticsDataType: "percentTwoDecimal",
    hasImpactCalculation: true,
    isEligibleForPrimaryMetric: true,
  },
  addToCartRate: {
    isStandard: true,
    id: "addToCartRate",
    identifier: "addToCartRate",
    name: "Add to Cart Rate",
    description: "Percentage of visitors who added a product to cart",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "add_to_cart_rate",
    analyticsDataType: "percentTwoDecimal",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  abandonedCartRate: {
    isStandard: true,
    id: "abandonedCartRate",
    identifier: "abandonedCartRate",
    name: "Abandoned Cart Rate",
    description:
      "Percentage of visitors who added to cart but did not make an order",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "abandoned_cart_rate",
    analyticsDataType: "percentTwoDecimal",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  abandonedCheckoutRate: {
    isStandard: true,
    id: "abandonedCheckoutRate",
    identifier: "abandonedCheckoutRate",
    name: "Abandoned Checkout Rate",
    description:
      "Percentage of visitors who started checkout but did not make an order",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "abandoned_checkout_rate",
    analyticsDataType: "percentTwoDecimal",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  checkoutBeginRate: {
    isStandard: true,
    id: "checkoutBeginRate",
    identifier: "checkoutBeginRate",
    name: "Checkout - begin rate",
    description: "Percentage of visitors who started checkout",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "checkout_begin_rate",
    analyticsDataType: "percentTwoDecimal",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  checkoutEnterContactInfoRate: {
    isStandard: true,
    id: "checkoutEnterContactInfoRate",
    identifier: "checkoutEnterContactInfoRate",
    name: "Checkout - enter contact info rate",
    description:
      "Percentage of visitors who entered contact information (email or phone number) at checkout",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "checkout_enter_contact_info_rate",
    analyticsDataType: "percentTwoDecimal",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  checkoutAddressSubmittedRate: {
    isStandard: true,
    id: "checkoutAddressSubmittedRate",
    identifier: "checkoutAddressSubmittedRate",
    name: "Checkout - enter address rate",
    description:
      "Percentage of visitors who entered their shipping address and saw shipping rates at checkout",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "checkout_address_submitted_rate",
    analyticsDataType: "percentTwoDecimal",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  viewProductPageRate: {
    isStandard: true,
    id: "viewProductPageRate",
    identifier: "viewProductPageRate",
    name: "View product page rate",
    description: "Percent of unique site visitors who viewed a product page",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "view_product_page_rate",
    analyticsDataType: "percentTwoDecimal",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  viewCollectionPageRate: {
    isStandard: true,
    id: "viewCollectionPageRate",
    identifier: "viewCollectionPageRate",
    name: "View Collection Page Rate",
    description: "Percentage of visitors who viewed a collection page",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "view_collection_page_rate",
    analyticsDataType: "percentTwoDecimal",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  netRevenuePerVisitor: {
    isStandard: true,
    id: "netRevenuePerVisitor",
    identifier: "netRevenuePerVisitor",
    name: "Revenue per Visitor",
    description:
      "Net revenue divided by number of unique visitors. Net revenue includes product and shipping revenue, net of discounts",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "net_revenue_per_visitor",
    analyticsDataType: "currency",
    hasImpactCalculation: true,
    isEligibleForPrimaryMetric: true,
  },
  netRevenuePerOrder: {
    isStandard: true,
    id: "netRevenuePerOrder",
    identifier: "netRevenuePerOrder",
    name: "Average Order Value",
    description:
      "Net revenue divided by number of orders (also known as average order value, AOV)",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "net_revenue_per_order",
    analyticsDataType: "currency",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  netProductRevenuePerOrder: {
    isStandard: true,
    id: "netProductRevenuePerOrder",
    identifier: "netProductRevenuePerOrder",
    description:
      "Average net revenue per order generated by product sales alone (i.e. excludes shipping revenue)",
    name: "Net Product Revenue per Order",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "net_product_revenue_per_order",
    analyticsDataType: "currency",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  netShippingRevenuePerOrder: {
    isStandard: true,
    id: "netShippingRevenuePerOrder",
    identifier: "netShippingRevenuePerOrder",
    name: "Net Shipping Revenue per Order",
    description:
      "Average net revenue per order generated by shipping charges. This is the shipping revenue portion of AOV.",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "net_shipping_revenue_per_order",
    analyticsDataType: "currency",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: false,
  },
  avgUnitsPerOrder: {
    isStandard: true,
    id: "avgUnitsPerOrder",
    identifier: "avgUnitsPerOrder",
    name: "Avg units per order",
    description: "Total unit quantity divided by number of orders",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "avg_units_per_order",
    analyticsDataType: "number",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  avgProductRevenuePerUnit: {
    isStandard: true,
    id: "avgProductRevenuePerUnit",
    identifier: "avgProductRevenuePerUnit",
    name: "Avg product revenue per unit",
    description:
      "Net product revenue divided by unit quantity (i.e., average net price paid per unit)",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "avg_product_revenue_per_unit",
    analyticsDataType: "currency",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  profitPerVisitor: {
    isStandard: true,
    id: "profitPerVisitor",
    identifier: "profitPerVisitor",
    name: "Profit per Visitor",
    description:
      "Gross profit (net revenue COGS, cost of shipping, and transaction fees) divided by number of unique visitors",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "gross_profit_per_visitor",
    analyticsDataType: "currency",
    hasImpactCalculation: true,
    isEligibleForPrimaryMetric: true,
  },
  subscriptionProductRevenuePerOrder: {
    isStandard: true,
    id: "subscriptionProductRevenuePerOrder",
    identifier: "subscriptionProductRevenuePerOrder",
    name: "Subscription AOV - product",
    description: "Average net revenue per new subscription order",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "subscription_product_revenue_per_order",
    analyticsDataType: "currency",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: false,
  },
  subscriptionRevenuePerOrder: {
    isStandard: true,
    id: "subscriptionRevenuePerOrder",
    identifier: "subscriptionRevenuePerOrder",
    name: "Subscription order AOV",
    description: "Average net revenue per new subscription order",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "subscription_revenue_per_order",
    analyticsDataType: "currency",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  subscriptionOrdersPerVisitor: {
    isStandard: true,
    id: "subscriptionOrdersPerVisitor",
    identifier: "subscriptionOrdersPerVisitor",
    name: "Subscription orders per visitor",
    description:
      "Number of new subscription orders divided by number of unique visitors",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "subscription_orders_per_visitor",
    analyticsDataType: "number",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  subscriptionRevenuePerVisitor: {
    isStandard: true,
    id: "subscriptionRevenuePerVisitor",
    identifier: "subscriptionRevenuePerVisitor",
    name: "Subscription revenue per visitor",
    description:
      "Net revenue from new subscription orders divided by number of unique visitors",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "subscription_revenue_per_visitor",
    analyticsDataType: "currency",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  subscriptionProfitPerVisitor: {
    isStandard: true,
    id: "subscriptionProfitPerVisitor",
    identifier: "subscriptionProfitPerVisitor",
    name: "Subscription profit per visitor",
    description:
      "Gross profit from new subscription orders divided by number of unique visitors",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "subscription_gross_profit_per_visitor",
    analyticsDataType: "currency",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  profitPerOrder: {
    isStandard: true,
    id: "profitPerOrder",
    identifier: "profitPerOrder",
    name: "Profit per Order",
    description: "Gross profit divided by number of orders",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "gross_profit_per_order",
    analyticsDataType: "currency",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  pctOrdersFreeShipping: {
    isStandard: true,
    id: "pctOrdersFreeShipping",
    identifier: "pctOrdersFreeShipping",
    name: "% Orders with Free Shipping",
    description: "Percentage of orders with no associated net shipping revenue",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "pct_orders_free_shipping",
    analyticsDataType: "percent",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: false,
  },
  avgDiscountPerDiscountedOrder: {
    isStandard: true,
    id: "avgDiscountPerDiscountedOrder",
    identifier: "avgDiscountPerDiscountedOrder",
    name: "Avg discount per discounted order",
    description: "Average discount per order with a discount applied",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "avg_discount_per_discounted_order",
    analyticsDataType: "currency",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  avgDiscountPerAllOrders: {
    isStandard: true,
    id: "avgDiscountPerAllOrders",
    identifier: "avgDiscountPerAllOrders",
    name: "Avg discount per all orders (i.e., GTN $)",
    description:
      "Average discount per order, including 0s from orders with no discount",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "avg_discount_per_all_orders",
    analyticsDataType: "currency",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  pctSubscriptionOrders: {
    isStandard: true,
    id: "pctSubscriptionOrders",
    identifier: "pctSubscriptionOrders",
    name: "Subscription % of orders",
    description: "Percentage of orders that contained a subscription",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "pct_subscription_orders",
    analyticsDataType: "percent",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: false,
  },
  pctOrdersWithPostPurchaseOffer: {
    isStandard: true,
    id: "pctOrdersWithPostPurchaseOffer",
    identifier: "pctOrdersWithPostPurchaseOffer",
    name: "% of Orders with Post Purchase Upsell",
    description: "Percentage of orders with a post purchase upsell offer",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "pct_orders_with_post_purchase_offer",
    analyticsDataType: "percentTwoDecimal",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  netRevenueFromPostPurchaseOffer: {
    isStandard: true,
    id: "netRevenueFromPostPurchaseOffer",
    identifier: "netRevenueFromPostPurchaseOffer",
    name: "Post Purchase Upsell Revenue",
    description: "Net revenue generated from post purchase upsell offers",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "net_revenue_from_post_purchase_offer",
    analyticsDataType: "currency",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  pctOrdersWithUpsell: {
    isStandard: true,
    id: "pctOrdersWithUpsell",
    identifier: "pctOrdersWithUpsell",
    name: "% of Orders with Checkout Upsell",
    description: "Percentage of orders with a checkout upsell",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "pct_orders_with_checkout_upsell",
    analyticsDataType: "percentTwoDecimal",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  netRevenueFromUpsell: {
    isStandard: true,
    id: "netRevenueFromUpsell",
    identifier: "netRevenueFromUpsell",
    name: "Checkout Upsell Revenue",
    description: "Net revenue generated from checkout upsells",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "net_revenue_from_checkout_upsell",
    analyticsDataType: "currency",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  pctOrdersWithOnsiteUpsell: {
    isStandard: true,
    id: "pctOrdersWithOnsiteUpsell",
    identifier: "pctOrdersWithOnsiteUpsell",
    name: "% of Orders with Onsite Upsell",
    description: "Percentage of orders with an onsite upsell",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "pct_orders_with_onsite_upsell",
    analyticsDataType: "percentTwoDecimal",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
  netRevenueFromOnsiteUpsell: {
    isStandard: true,
    id: "netRevenueFromOnsiteUpsell",
    identifier: "netRevenueFromOnsiteUpsell",
    name: "Onsite Upsell Revenue",
    description: "Net revenue generated from onsite upsells",
    eventCountLastDay: null,
    lastEventTs: null,
    analyticsMetricId: "net_revenue_from_onsite_upsell",
    analyticsDataType: "currency",
    hasImpactCalculation: false,
    isEligibleForPrimaryMetric: true,
  },
};

export const STANDARD_EVENTS_DISALLOWED_KEY_METRICS: Record<
  StandardEventDisallowedKeyMetrics,
  StandardEventDto & StandardEventAnalyticsMetricType
> = {
  pctDiscountedOrders: {
    isStandard: true,
    id: "pctDiscountedOrders",
    identifier: "pctDiscountedOrders",
    name: "% of orders w/ a discount",
    description: "Percentage of orders with a discount applied",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "pct_discounted_orders",
    analyticsDataType: "percent",
    isEligibleForPrimaryMetric: false,
  },
  pctOrdersWithUpsell: {
    isStandard: true,
    id: "pctOrdersWithUpsell",
    identifier: "pctOrdersWithUpsell",
    name: "% of orders w/ an upsell product",
    description: "Percentage of orders with an upsell product",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "pct_orders_with_checkout_upsell",
    analyticsDataType: "percent",
    isEligibleForPrimaryMetric: false,
  },
  pctOrdersWithOnsiteUpsell: {
    isStandard: true,
    id: "pctOrdersWithOnsiteUpsell",
    identifier: "pctOrdersWithOnsiteUpsell",
    name: "% of orders w/ an onsite upsell product",
    description: "Percentage of orders with an onsite upsell product",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "pct_orders_with_onsite_upsell",
    analyticsDataType: "percent",
    isEligibleForPrimaryMetric: false,
  },
  nOrdersWithUpsellProduct: {
    isStandard: true,
    id: "nOrdersWithUpsellProduct",
    identifier: "nOrdersWithUpsellProduct",
    name: "Number of orders w/ an upsell product",
    description: "Number of orders w/ an upsell product",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "n_orders_with_checkout_upsell_product",
    analyticsDataType: "numberNoDecimal",
    isEligibleForPrimaryMetric: false,
  },
  nOrdersWithOnsiteUpsellProduct: {
    isStandard: true,
    id: "nOrdersWithOnsiteUpsellProduct",
    identifier: "nOrdersWithOnsiteUpsellProduct",
    name: "Number of orders w/ an onsite upsell product",
    description: "Number of orders w/ an onsite upsell product",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "n_orders_with_onsite_upsell_product",
    analyticsDataType: "numberNoDecimal",
    isEligibleForPrimaryMetric: false,
  },
  nOrdersDiscounted: {
    isStandard: true,
    id: "nOrdersDiscounted",
    identifier: "nOrdersDiscounted",
    name: "Number of orders discounted",
    description: "Number of orders with a discount applied",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "n_orders_discounted",
    analyticsDataType: "numberNoDecimal",
    isEligibleForPrimaryMetric: false,
  },
  discountPctDiscountedOrderGrossRevenue: {
    isStandard: true,
    id: "discountPctDiscountedOrderGrossRevenue",
    identifier: "discountPctDiscountedOrderGrossRevenue",
    name: "Discount % of gross revenue, discounted orders",
    description:
      "Discounts divided by gross revenue from orders with a discount",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "discount_pct_discounted_order_gross_revenue",
    analyticsDataType: "percent",
    isEligibleForPrimaryMetric: false,
  },
  discountPctTotalGrossRevenue: {
    isStandard: true,
    id: "discountPctTotalGrossRevenue",
    identifier: "discountPctTotalGrossRevenue",
    name: "Discount % of gross revenue, all orders (i.e., GTN %)",
    description: "Discounts divided by total gross revenue",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "discount_pct_total_gross_revenue",
    analyticsDataType: "percent",
    isEligibleForPrimaryMetric: false,
  },
  discountPctDiscountedOrderGrossProfit: {
    isStandard: true,
    id: "discountPctDiscountedOrderGrossProfit",
    identifier: "discountPctDiscountedOrderGrossProfit",
    name: "Discount % of gross profit, discounted orders",
    description:
      "Discounts divided by gross profit from orders with a discount",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "discount_pct_discounted_order_gross_profit",
    analyticsDataType: "percent",
    isEligibleForPrimaryMetric: false,
  },
  discountPctTotalGrossProfit: {
    isStandard: true,
    id: "discountPctTotalGrossProfit",
    identifier: "discountPctTotalGrossProfit",
    name: "Discount % of gross profit, all orders",
    description: "Discounts divided by total gross profit",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "discount_pct_total_gross_profit",
    analyticsDataType: "percent",
    isEligibleForPrimaryMetric: false,
  },
  subscriptionProductRevenuePerUnit: {
    isStandard: true,
    id: "subscriptionProductRevenuePerUnit",
    identifier: "subscriptionProductRevenuePerUnit",
    name: "Subscription Product Revenue per Unit",
    description:
      "Net product revenue from new subscription orders divided by total unit quantity from new subscription orders",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "subscription_product_revenue_per_unit",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  subscriptionProductRevenue: {
    isStandard: true,
    id: "subscriptionProductRevenue",
    identifier: "subscriptionProductRevenue",
    name: "Subscription Product Revenue",
    description: "Net product revenue from new subscription orders",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "subscription_product_revenue",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  subscriptionShippingRevenuePerOrder: {
    isStandard: true,
    id: "subscriptionShippingRevenuePerOrder",
    identifier: "subscriptionShippingRevenuePerOrder",
    name: "Subscription AOV - shipping",
    description:
      "Average net revenue from shipping fees per new subscription order",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "subscription_shipping_revenue_per_order",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  subscriptionUnitsPerSubscriptionOrder: {
    isStandard: true,
    id: "subscriptionUnitsPerSubscriptionOrder",
    identifier: "subscriptionUnitsPerSubscriptionOrder",
    name: "Subscription Avg Units per Order",
    description:
      "Total unit quantity from new subscription orders divided by number of new subscription orders",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "subscription_units_per_subscription_order",
    analyticsDataType: "numberNoDecimal",
    isEligibleForPrimaryMetric: false,
  },
  nSubscriptionOrders: {
    isStandard: true,
    id: "nSubscriptionOrders",
    identifier: "nSubscriptionOrders",
    name: "Number of Subscription Orders",
    description: "Number of Subscription Orders",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "n_subscription_orders",
    analyticsDataType: "numberNoDecimal",
    isEligibleForPrimaryMetric: false,
  },
  netSubscriptionRevenue: {
    isStandard: true,
    id: "netSubscriptionRevenue",
    identifier: "netSubscriptionRevenue",
    name: "Subscription Revenue",
    description: "Net revenue generated by subscription orders",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "subscription_net_revenue",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  subscriptionShippingRevenue: {
    isStandard: true,
    id: "subscriptionShippingRevenue",
    identifier: "subscriptionShippingRevenue",
    name: "Subscription Shipping Revenue",
    description: "Net shipping revenue generated by subscription orders",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "subscription_shipping_revenue",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  totalSubscriptionProfit: {
    isStandard: true,
    id: "totalSubscriptionProfit",
    identifier: "totalSubscriptionProfit",
    name: "Total Subscription Profit",
    description: "Gross profit from subscription orders",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "subscription_gross_profit",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  subscriptionUnits: {
    isStandard: true,
    id: "subscriptionUnits",
    identifier: "subscriptionUnits",
    name: "Number of Subscription Units",
    description: "Number of subscription units sold",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "subscription_units",
    analyticsDataType: "numberNoDecimal",
    isEligibleForPrimaryMetric: false,
  },
  netProductRevenue: {
    isStandard: true,
    id: "netProductRevenue",
    identifier: "netProductRevenue",
    name: "Product revenue",
    description:
      "Net revenue generated by product sales alone (i.e., excludes shipping revenue)",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "net_product_revenue",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  netRevenueFromUpsell: {
    isStandard: true,
    id: "netRevenueFromUpsell",
    identifier: "netRevenueFromUpsell",
    name: "Net revenue from upsell products",
    description: "Net revenue from upsell products",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "net_revenue_from_checkout_upsell",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  netRevenueFromOnsiteUpsell: {
    isStandard: true,
    id: "netRevenueFromOnsiteUpsell",
    identifier: "netRevenueFromOnsiteUpsell",
    name: "Net revenue from onsite upsell products",
    description: "Net revenue from onsite upsell products",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "net_revenue_from_onsite_upsell",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  totalCogs: {
    isStandard: true,
    id: "totalCogs",
    identifier: "totalCogs",
    name: "COGS",
    description: "Total cost of goods sold",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "total_cogs",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  productProfit: {
    isStandard: true,
    id: "productProfit",
    identifier: "productProfit",
    name: "Product profit",
    description: "Net product revenue minus COGS",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "product_profit",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  netShippingRevenue: {
    isStandard: true,
    id: "netShippingRevenue",
    identifier: "netShippingRevenue",
    name: "Shipping revenue",
    description: "Revenue from shipping fees, net of discounts",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "net_shipping_revenue",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  totalShippingCost: {
    isStandard: true,
    id: "totalShippingCost",
    identifier: "totalShippingCost",
    name: "Shipping cost",
    description:
      "Cost of shipping (shipping fulfillment cost per order times number of orders)",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "total_shipping_cost",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  shippingProfit: {
    isStandard: true,
    id: "shippingProfit",
    identifier: "shippingProfit",
    name: "Shipping profit",
    description: "Net shipping revenue minus total shipping cost",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "shipping_profit",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  totalTransactionFee: {
    isStandard: true,
    id: "totalTransactionFee",
    identifier: "totalTransactionFee",
    name: "Transaction fees",
    description: "Transaction fees percent times net revenue",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "total_transaction_fee",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  totalTaxIncluded: {
    isStandard: true,
    id: "totalTaxIncluded",
    identifier: "totalTaxIncluded",
    name: "Total Tax Included",
    description: "Total tax included in the order",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "total_tax_included",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  netRevenue: {
    isStandard: true,
    id: "netRevenue",
    identifier: "netRevenue",
    name: "Net revenue",
    description:
      "Revenue (both product revenue and shipping fees), net of discounts",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "net_revenue",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  totalCost: {
    isStandard: true,
    id: "totalCost",
    identifier: "totalCost",
    name: "Total cost",
    description:
      "Total cost, including COGS, shipping cost, and transaction fees",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "gross_cost",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  totalProfit: {
    isStandard: true,
    id: "totalProfit",
    identifier: "totalProfit",
    name: "Gross profit",
    description:
      "Net revenue minus total cost (COGS, cost of shipping, and transaction fees)",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "gross_profit",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  grossMarginPct: {
    isStandard: true,
    id: "grossMarginPct",
    identifier: "grossMarginPct",
    name: "Gross margin %",
    description: "Gross profit divided by net revenue",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "gross_margin_pct",
    analyticsDataType: "percentTwoDecimal",
    isEligibleForPrimaryMetric: false,
  },
  nVisitors: {
    isStandard: true,
    id: "nVisitors",
    identifier: "nVisitors",
    name: "Visitors",
    description: "Number of unique visitors",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "n_visitors",
    analyticsDataType: "numberNoDecimal",
    isEligibleForPrimaryMetric: false,
  },
  nOrders: {
    isStandard: true,
    id: "nOrders",
    identifier: "nOrders",
    name: "Orders",
    description: "Number of orders made by visitors to the experience",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "n_orders",
    analyticsDataType: "numberNoDecimal",
    isEligibleForPrimaryMetric: false,
  },
  nVisitorsAddedToCart: {
    isStandard: true,
    id: "nVisitorsAddedToCart",
    identifier: "nVisitorsAddedToCart",
    name: "Number of visitors added to cart",
    description: "The number of visitors that added a product to the cart",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "n_visitors_added_to_cart",
    analyticsDataType: "numberNoDecimal",
    isEligibleForPrimaryMetric: false,
  },
  nVisitorsCheckoutStarted: {
    isStandard: true,
    id: "nVisitorsCheckoutStarted",
    identifier: "nVisitorsCheckoutStarted",
    name: "Number of visitors that started checkout",
    description: "The number of visitors that started the checkout process",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "n_visitors_checkout_started",
    analyticsDataType: "numberNoDecimal",
    isEligibleForPrimaryMetric: false,
  },
  nVisitorsAbandonedCart: {
    isStandard: true,
    id: "nVisitorsAbandonedCart",
    identifier: "nVisitorsAbandonedCart",
    name: "Number of visitors that abandoned cart",
    description:
      "The number of visitors that added to cart but did not make an order",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "n_visitors_abandoned_cart",
    analyticsDataType: "numberNoDecimal",
    isEligibleForPrimaryMetric: false,
  },
  nVisitorsAbandonedCheckout: {
    isStandard: true,
    id: "nVisitorsAbandonedCheckout",
    identifier: "nVisitorsAbandonedCheckout",
    name: "Number of visitors that abandoned checkout",
    description:
      "The number of visitors that started checkout but did not make an order",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "n_visitors_abandoned_checkout",
    analyticsDataType: "numberNoDecimal",
    isEligibleForPrimaryMetric: false,
  },
  nVisitorsCheckoutContactInfoSubmitted: {
    isStandard: true,
    id: "nVisitorsCheckoutContactInfoSubmitted",
    identifier: "nVisitorsCheckoutContactInfoSubmitted",
    name: "Number of visitors that entered contact info",
    description:
      "The number of visitors that entered contact information at checkout",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "n_visitors_checkout_contact_info_submitted",
    analyticsDataType: "numberNoDecimal",
    isEligibleForPrimaryMetric: false,
  },
  nVisitorsCheckoutAddressInfoSubmitted: {
    isStandard: true,
    id: "nVisitorsCheckoutAddressInfoSubmitted",
    identifier: "nVisitorsCheckoutAddressInfoSubmitted",
    name: "Number of visitors that entered address info",
    description:
      "The number of visitors that entered shipping address at checkout",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "n_visitors_checkout_address_info_submitted",
    analyticsDataType: "numberNoDecimal",
    isEligibleForPrimaryMetric: false,
  },
  nVisitorsViewedProductPage: {
    isStandard: true,
    id: "nVisitorsViewedProductPage",
    identifier: "nVisitorsViewedProductPage",
    name: "Number of visitors that viewed product page",
    description: "The number of visitors that viewed a product page",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "n_visitors_viewed_product_page",
    analyticsDataType: "numberNoDecimal",
    isEligibleForPrimaryMetric: false,
  },
  nVisitorsViewedCollectionPage: {
    isStandard: true,
    id: "nVisitorsViewedCollectionPage",
    identifier: "nVisitorsViewedCollectionPage",
    name: "Number of visitors that viewed collection page",
    description: "The number of visitors that viewed a collection page",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "n_visitors_viewed_collection_page",
    analyticsDataType: "numberNoDecimal",
    isEligibleForPrimaryMetric: false,
  },
  // Post-test metrics
  nCustomers: {
    isStandard: true,
    id: "nCustomers",
    identifier: "nCustomers",
    name: "In-Test Customers",
    description:
      "Number of unique customers who placed at least one post-experiment order",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "n_customers",
    analyticsDataType: "numberNoDecimal",
    isEligibleForPrimaryMetric: false,
  },
  nPostOrders: {
    isStandard: true,
    id: "nPostOrders",
    identifier: "nPostOrders",
    name: "Post-Test Orders",
    description: "Number of post-experiment orders",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "n_post_orders",
    analyticsDataType: "numberNoDecimal",
    isEligibleForPrimaryMetric: false,
  },
  postNetRevenue: {
    isStandard: true,
    id: "postNetRevenue",
    identifier: "postNetRevenue",
    name: "Post-Test Net Revenue",
    description: "Net revenue from post-experiment orders",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "post_net_revenue",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  postGrossProfit: {
    isStandard: true,
    id: "postGrossProfit",
    identifier: "postGrossProfit",
    name: "Post-Test Gross Profit",
    description: "Gross profit from post-experiment orders",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "post_gross_profit",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  pctRepeatCustomers: {
    isStandard: true,
    id: "pctRepeatCustomers",
    identifier: "pctRepeatCustomers",
    name: "% Customers with Post-Test Order",
    description: "Percentage of customers who placed more than one order",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "pct_repeat_customers",
    analyticsDataType: "percentTwoDecimal",
    isEligibleForPrimaryMetric: false,
  },
  postOrdersPerCustomer: {
    isStandard: true,
    id: "postOrdersPerCustomer",
    identifier: "postOrdersPerCustomer",
    name: "Post-Test Orders Per Customer",
    description: "Post-experiment orders divided by number of customers",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "post_orders_per_customer",
    analyticsDataType: "number",
    isEligibleForPrimaryMetric: false,
  },
  postOrdersPerVisitor: {
    isStandard: true,
    id: "postOrdersPerVisitor",
    identifier: "postOrdersPerVisitor",
    name: "Post-Test Orders Per Visitor",
    description: "Post-experiment orders divided by number of unique visitors",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "post_orders_per_visitor",
    analyticsDataType: "number",
    isEligibleForPrimaryMetric: false,
  },
  postRevenuePerCustomer: {
    isStandard: true,
    id: "postRevenuePerCustomer",
    identifier: "postRevenuePerCustomer",
    name: "Post-Test Revenue Per Customer",
    description: "Post-experiment net revenue divided by number of customers",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "post_revenue_per_customer",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  postRevenuePerVisitor: {
    isStandard: true,
    id: "postRevenuePerVisitor",
    identifier: "postRevenuePerVisitor",
    name: "Post-Test Revenue Per Visitor",
    description:
      "Post-experiment net revenue divided by number of unique visitors",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "post_revenue_per_visitor",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  postProfitPerCustomer: {
    isStandard: true,
    id: "postProfitPerCustomer",
    identifier: "postProfitPerCustomer",
    name: "Post-Test Profit Per Customer",
    description: "Post-experiment gross profit divided by number of customers",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "post_profit_per_customer",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
  postProfitPerVisitor: {
    isStandard: true,
    id: "postProfitPerVisitor",
    identifier: "postProfitPerVisitor",
    name: "Post-Test Profit Per Visitor",
    description:
      "Post-experiment gross profit divided by number of unique visitors",
    eventCountLastDay: null,
    lastEventTs: null,
    hasImpactCalculation: false,
    analyticsMetricId: "post_profit_per_visitor",
    analyticsDataType: "currency",
    isEligibleForPrimaryMetric: false,
  },
};

export const STANDARD_EVENTS: Record<StandardEventType, StandardEventDto> = {
  ...STANDARD_EVENTS_ALLOWED_KEY_METRICS,
  ...STANDARD_EVENTS_DISALLOWED_KEY_METRICS,
};
